import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from '../../components/ui/tooltip';
import { House, Gear, Users } from '@phosphor-icons/react';

/**
 * Shared scaffold for the Mobile Apps management pages (Ecoplus & Ecofront).
 * Matches the Configurations page structure: minimal title + tab bar.
 *
 * `renderAppUsers`, `renderOverview`, `renderConfigurations` — optional slot
 * renderers so each page can inject its own tab content without duplicating
 * the tabbed shell.
 */
export function MobileAppScaffold({
  title,
  renderOverview,
  renderConfigurations,
  renderAppUsers,
}) {
  const slug = title.toLowerCase();
  return (
    <div className="space-y-6" data-testid={`${slug}-manage-page`}>
      {/* Header — matches ConfigurationsPage style */}
      <div>
        <h1
          className="text-3xl font-bold text-[#1C1917] tracking-tight"
          style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
        >
          {title}
        </h1>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="w-full">
        <TooltipProvider delayDuration={150}>
          <TabsList className="bg-white border border-[#EDE6D8] rounded-lg p-1 h-auto inline-flex gap-1 w-auto">
            <Tooltip>
              <TooltipTrigger asChild>
                <TabsTrigger
                  value="overview"
                  className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white rounded-lg h-10 px-3 gap-2 text-sm"
                  data-testid={`tab-${slug}-overview`}
                >
                  <House size={16} />
                  <span className="font-semibold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Overview</span>
                </TabsTrigger>
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={6}>Overview</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <TabsTrigger
                  value="configurations"
                  className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white rounded-lg h-10 px-3 gap-2 text-sm"
                  data-testid={`tab-${slug}-configurations`}
                >
                  <Gear size={16} />
                  <span className="font-semibold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Configurations</span>
                </TabsTrigger>
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={6}>Configurations</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <TabsTrigger
                  value="app-users"
                  className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white rounded-lg h-10 px-3 gap-2 text-sm"
                  data-testid={`tab-${slug}-app-users`}
                >
                  <Users size={16} />
                  <span className="font-semibold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>App Users</span>
                </TabsTrigger>
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={6}>App Users</TooltipContent>
            </Tooltip>
          </TabsList>
        </TooltipProvider>

        <TabsContent value="overview" className="mt-6">{renderOverview ? renderOverview() : null}</TabsContent>
        <TabsContent value="configurations" className="mt-6">{renderConfigurations ? renderConfigurations() : null}</TabsContent>
        <TabsContent value="app-users" className="mt-6">{renderAppUsers ? renderAppUsers() : null}</TabsContent>
      </Tabs>
    </div>
  );
}
